/**
 * Checks if a hostname matches a given pattern.
 *
 * Supports multiple pattern types:
 * - Exact match: "app.hubspot.com"
 * - Wildcard subdomain: "*.hubspot.com" (matches any subdomain)
 * - Prefix match: "app*.hubspot.com" (matches subdomains starting with "app")
 * - Suffix match: ".hubspot.com" (matches any subdomain or exact match)
 * - Regex pattern: /^app\.hubspot\.com$/
 *
 * @param hostname - The hostname to check
 * @param pattern - The pattern to match against (string or RegExp)
 * @returns true if the hostname matches the pattern, false otherwise
 *
 * @example
 * matchesHostnamePattern('app.hubspot.com', 'app.hubspot.com') // true
 * matchesHostnamePattern('app.hubspot.com', '*.hubspot.com') // true
 * matchesHostnamePattern('app-eu1.hubspot.com', 'app*.hubspot.com') // true
 * matchesHostnamePattern('app.hubspot.com', '.hubspot.com') // true
 * matchesHostnamePattern('app.hubspot.com', /^app\.hubspot\.com$/) // true
 */
export const matchesHostnamePattern = (hostname, pattern) => {
  if (!hostname || !pattern) {
    return false;
  }
  if (pattern instanceof RegExp) {
    return pattern.test(hostname);
  }
  const patternStr = pattern.trim();
  if (patternStr.startsWith('*.')) {
    const suffix = patternStr.slice(2);
    return hostname === suffix || hostname.endsWith(`.${suffix}`);
  }
  if (patternStr.startsWith('.')) {
    return hostname === patternStr.slice(1) || hostname.endsWith(patternStr);
  }

  // Handle prefix match with wildcard (e.g., "app*.hubspot.com")
  if (patternStr.includes('*')) {
    const wildcardIndex = patternStr.indexOf('*');
    const prefix = patternStr.slice(0, wildcardIndex);
    const suffix = patternStr.slice(wildcardIndex + 1);
    if (!hostname.startsWith(prefix) || !hostname.endsWith(suffix)) {
      return false;
    }

    // Extract the middle part between prefix and suffix
    const middlePart = hostname.slice(prefix.length, hostname.length - suffix.length);

    // For patterns like "app*.hubspot.com":
    // - Allow empty middle (exact match: "app.hubspot.com")
    // - Allow middle starting with hyphen (hublets: "app-eu1.hubspot.com")
    // - Disallow middle starting with letters (prevents "application.hubspot.com")
    return middlePart === '' || middlePart.startsWith('-');
  }
  return hostname === patternStr;
};

/**
 * Checks if a hostname matches any of the given patterns.
 *
 * @param hostname - The hostname to check
 * @param patterns - Array of patterns to match against
 * @returns true if the hostname matches any pattern, false otherwise
 *
 * @example
 * matchesAnyHostnamePattern('app.hubspot.com', ['*.hubspot.com', '*.hubspotqa.com']) // true
 */
export const matchesAnyHostnamePattern = (hostname, patterns) => {
  return patterns.some(pattern => matchesHostnamePattern(hostname, pattern));
};

/**
 * Checks if a hostname is an internal HubSpot domain (hubteam.com or hubteamqa.com).
 *
 * @param hostname - The hostname to check
 * @returns true if the hostname matches internal HubSpot patterns, false otherwise
 *
 * @example
 * isInternalHostname('app.hubteam.com') // true
 * isInternalHostname('app.hubteamqa.com') // true
 * isInternalHostname('app.hubspot.com') // false
 */
export const isInternalHostname = hostname => {
  return matchesAnyHostnamePattern(hostname, ['*.hubteam.com', '*.hubteamqa.com']);
};

/**
 * Checks if a hostname is an app-auth HubSpot domain.
 * Matches the "app" subdomain and hublet variants (app-eu1, app-na1, app-ap1, etc.).
 * Does NOT match other subdomains like "ecosystem" or "api".
 *
 * @param hostname - The hostname to check
 * @returns true if the hostname matches app-auth HubSpot patterns, false otherwise
 *
 * @example
 * isAppAuthHostname('app.hubspot.com') // true
 * isAppAuthHostname('app-eu1.hubspot.com') // true
 * isAppAuthHostname('app.hubspotqa.com') // true
 * isAppAuthHostname('ecosystem.hubspot.com') // false
 * isAppAuthHostname('api.hubspot.com') // false
 * isAppAuthHostname('app.hubteam.com') // false
 */
export const isAppAuthHostname = hostname => {
  return matchesAnyHostnamePattern(hostname, ['app*.hubspot.com', 'app*.hubspotqa.com']);
};